import React, { useState, useEffect } from "react";
import { HiArrowLeft } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { usePersonalizedInsights } from "../../hooks/usePersonalizedInsights";
import { useAuth } from "../../contexts/AuthContext";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { aiUsageService } from "../../services/aiUsageService";
import { allowanceRefusalMessage } from "../../services/aiClient";
import { MonthlyUsageSummary } from "../../types/usage";
import { HiChevronLeft as HiChevronLeftSolid } from "react-icons/hi";
import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { SparklesIcon } from "@heroicons/react/24/solid";
import Header from "../../components/layout/Header";
import { useSubscription } from "../../providers/SubscriptionProvider";
import ComingSoon from "../../components/ComingSoon";
import { userService } from "../../services/userService";

const AiSettings: React.FC = () => {
  const navigate = useNavigate();
  const {
    personalizedInsightsEnabled,
    setPersonalizedInsightsEnabled,
    isLoading: insightsLoading,
  } = usePersonalizedInsights();
  const { currentUser } = useAuth();
  const { isDefault, isPremium } = useSubscription();

  const [customToneEnabled, setCustomToneEnabled] = useState(false);
  const [customToneText, setCustomToneText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [usageSummary, setUsageSummary] = useState<MonthlyUsageSummary | null>(
    null,
  );
  const [usageLoading, setUsageLoading] = useState(false);
  const [showToneInfoDialog, setShowToneInfoDialog] = useState(false);

  // AI Goals state
  const [performanceGoals, setPerformanceGoals] = useState<string>("");
  const [healthGoals, setHealthGoals] = useState<string>("");
  const [goalsSaving, setGoalsSaving] = useState(false);


  useEffect(() => {
    if (currentUser) {
      loadCustomTone();
      loadUsageData();
      loadGoals();
    }
  }, [currentUser]);

  const loadGoals = async () => {
    if (!currentUser?.uid) return;
    try {
      const { userData } = await userService.getUserProfile(currentUser.uid);
      if (userData) {
        setPerformanceGoals((userData as any).performanceGoals || "");
        setHealthGoals((userData as any).healthGoals || "");
      }
    } catch (error) {
      console.error("Error loading goals:", error);
    }
  };

  const saveGoals = async () => {
    if (!currentUser?.uid) return;
    try {
      setGoalsSaving(true);
      await userService.updateUserProfile(currentUser.uid, {
        performanceGoals: performanceGoals.trim(),
        healthGoals: healthGoals.trim(),
      });
    } catch (error) {
      console.error("Error saving goals:", error);
    } finally {
      setGoalsSaving(false);
    }
  };

  const loadCustomTone = async () => {
    if (!currentUser) return;

    try {
      setIsLoading(true);
      const toneDoc = await getDoc(
        doc(db, "systemInstructions", currentUser.uid, "tone", "tone_1"),
      );

      if (toneDoc.exists()) {
        const data = toneDoc.data();
        setCustomToneEnabled(data.enabled || false);
        setCustomToneText(data.toneText || "");
      }
    } catch (error) {
      console.error("Error loading custom tone:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveCustomTone = async (showAlert: boolean = true) => {
    if (!currentUser || !customToneText.trim()) return;

    try {
      setIsSaving(true);
      await setDoc(
        doc(db, "systemInstructions", currentUser.uid, "tone", "tone_1"),
        {
          toneText: customToneText.trim(),
          updatedAt: new Date(),
          enabled: customToneEnabled,
        },
      );

      if (showAlert) {
        alert("Custom tone saved successfully!");
      }
    } catch (error) {
      console.error("Error saving custom tone:", error);
      if (showAlert) {
        alert("Failed to save custom tone. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomToneToggle = async (enabled: boolean) => {
    setCustomToneEnabled(enabled);

    if (!enabled) {
      setCustomToneText("");
      // Update Firestore to disable custom tone if there was previously saved data
      if (currentUser) {
        try {
          setIsSaving(true);
          const toneDoc = await getDoc(
            doc(db, "systemInstructions", currentUser.uid, "tone", "tone_1"),
          );

          if (toneDoc.exists()) {
            await setDoc(
              doc(db, "systemInstructions", currentUser.uid, "tone", "tone_1"),
              {
                ...toneDoc.data(),
                enabled: false,
                updatedAt: new Date(),
              },
            );
          }
        } catch (error) {
          console.error("Error disabling custom tone:", error);
        } finally {
          setIsSaving(false);
        }
      }
    } else {
      // When enabling, load existing instructions from Firestore
      if (currentUser) {
        try {
          setIsLoading(true);
          const toneDoc = await getDoc(
            doc(db, "systemInstructions", currentUser.uid, "tone", "tone_1"),
          );

          if (toneDoc.exists()) {
            const data = toneDoc.data();
            setCustomToneText(data.toneText || "");
            // Update the enabled status in Firestore
            await setDoc(
              doc(db, "systemInstructions", currentUser.uid, "tone", "tone_1"),
              {
                ...data,
                enabled: true,
                updatedAt: new Date(),
              },
            );
          }
        } catch (error) {
          console.error("Error enabling custom tone:", error);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const loadUsageData = async () => {
    if (!currentUser) return;

    try {
      setUsageLoading(true);
      const summary = await aiUsageService.getCurrentUsageSummary(
        currentUser.uid,
      );
      setUsageSummary(summary);
    } catch (error) {
      console.error("Error loading usage data:", error);
    } finally {
      setUsageLoading(false);
    }
  };

  const formatTokenCount = (tokens: number): string => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  const getUsagePercentage = (tokens: number): number => {
    const limit = usageSummary?.limit ?? 0;
    if (limit <= 0) return tokens > 0 ? 100 : 0;
    return Math.min((tokens / limit) * 100, 100);
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="w-full pt-16">
      <Header />
      <div className="container max-w-2xl mx-auto py-8 px-8 flex flex-col">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between pb-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
            >
              <HiChevronLeftSolid className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content ml-4">
              AI Settings
            </h1>
          </div>
          <button
            onClick={() => setShowToneInfoDialog(true)}
            className="p-1 text-base-content/60 hover:text-base-content/80 transition-colors"
            aria-label="Information about AI Settings"
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Settings Content */}
        <div className="flex flex-col gap-4 relative">
          {!isPremium && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg">
              <ComingSoon featureKey="customInsightsTone" />
            </div>
          )}
          <div
            className={
              !isPremium
                ? "blur-[1px] pointer-events-none opacity-60 space-y-4"
                : "space-y-4"
            }
          >
            {/* Personalized AI Insights Setting */}
            <div className="bg-base-100 rounded-lg border border-base-300 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-base-content mb-2">
                    Personalized AI Insights
                  </h3>
                  <p className="text-sm text-base-content/70">
                    Show personalized AI insights on your dashboard and insights
                    pages based on your training and wellness data.
                  </p>
                </div>
                <div className="ml-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={personalizedInsightsEnabled}
                      onChange={(e) =>
                        setPersonalizedInsightsEnabled(e.target.checked)
                      }
                      className="sr-only peer"
                      disabled={insightsLoading}
                    />
                    <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-neutral peer-disabled:opacity-50"></div>
                  </label>
                </div>
              </div>

              {!personalizedInsightsEnabled && !insightsLoading && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    When disabled, you won't see personalized AI insights on
                    your dashboard or insights pages.
                  </p>
                </div>
              )}
            </div>
            {/* Custom Tone */}
            <div className="bg-base-100 rounded-lg border border-base-300 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-base-content mb-2">
                    Custom Tone
                  </h3>
                  <p className="text-sm text-base-content/70">
                    Describe how you would like your AI insights to talk to you.
                  </p>
                </div>
                <div className="ml-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customToneEnabled}
                      onChange={(e) => handleCustomToneToggle(e.target.checked)}
                      className="sr-only peer"
                      disabled={isLoading}
                    />
                    <div className="w-11 h-6 bg-base-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-base-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-base-100 after:border-base-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-neutral disabled:opacity-50"></div>
                  </label>
                </div>
              </div>

              {customToneEnabled && (
                <div className="mt-4 space-y-4">
                  <div className=" w-full">
                    <textarea
                      value={customToneText}
                      onChange={(e) => setCustomToneText(e.target.value)}
                      onBlur={() => saveCustomTone(false)}
                      placeholder="Describe how you'd like the AI to respond. For example: 'Be more casual and friendly' or 'Use technical language and be concise'..."
                      className="resize-none rounded-md p-4 placeholder:text-base-content/60 bg-base-300 focus:outline-none"
                      rows={4}
                      disabled={isLoading || isSaving}
                      style={{
                        height: "auto",
                        minHeight: "100px",
                        fontSize: "16px", // Prevents iOS zoom on focus
                        maxWidth: "100%",
                        width: "100%",
                      }}
                    />
                  </div>

                  <button
                    onClick={() => saveCustomTone(true)}
                    disabled={!customToneText.trim() || isSaving || isLoading}
                    className="bg-neutral w-full text-white font-grotesk px-4 py-2 rounded-md hover:!bg-neutral/90 shadow-none"
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                </div>
              )}

              {!customToneEnabled && (
                <div className="mt-4 p-3 bg-base-200 border border-base-300 rounded-md">
                  <p className="text-sm text-base-content/70">
                    Enable custom tone to personalize AI responses with your own
                    style preferences.
                  </p>
                </div>
              )}
            </div>

            {/* AI Usage Section */}
            <div className="bg-base-100 rounded-lg border border-base-300 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-base-content">
                    AI Usage This Month
                  </h3>
                  <p className="text-sm text-base-content/70">
                    Your AI token usage across the Peak apps
                  </p>
                </div>
                {usageLoading && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-base-content"></div>
                )}
              </div>

              {usageSummary && (
                <div className="space-y-4">
                  {/* Overall Usage Progress */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-base-content/80">
                        Total Usage
                      </span>
                      <span className="text-sm text-base-content">
                        {formatTokenCount(usageSummary.totalTokens)} /{" "}
                        {formatTokenCount(usageSummary.limit)} tokens
                      </span>
                    </div>
                    <div className="w-full bg-base-300 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(
                          getUsagePercentage(usageSummary.totalTokens),
                        )}`}
                        style={{
                          width: `${getUsagePercentage(
                            usageSummary.totalTokens,
                          )}%`,
                        }}
                      ></div>
                    </div>
                    <p className="text-xs text-base-content/60 mt-1">
                      {getUsagePercentage(usageSummary.totalTokens).toFixed(1)}%
                      of monthly limit used
                    </p>
                  </div>

                  {/* Usage Breakdown */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-base-200">
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-blue-600">
                        {formatTokenCount(usageSummary.chatUsage.tokens)}
                      </div>
                      <div className="text-xs text-base-content/60">
                        Chat Tokens
                      </div>
                      <div className="text-xs text-base-content/50">
                        {usageSummary.chatUsage.requests} requests
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-green-600">
                        {formatTokenCount(usageSummary.insightsUsage.tokens)}
                      </div>
                      <div className="text-xs text-base-content/60">
                        Insights Tokens
                      </div>
                      <div className="text-xs text-base-content/50">
                        {usageSummary.insightsUsage.requests} requests
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-semibold text-purple-600">
                        {formatTokenCount(usageSummary.sessionBuilderUsage.tokens)}
                      </div>
                      <div className="text-xs text-base-content/60">
                        Session Builder Tokens
                      </div>
                      <div className="text-xs text-base-content/50">
                        {usageSummary.sessionBuilderUsage.requests} requests
                      </div>
                    </div>
                  </div>

                  {/* Warning if approaching limit */}
                  {getUsagePercentage(usageSummary.totalTokens) >= 80 && (
                    <div
                      className={`p-3 rounded-md ${
                        getUsagePercentage(usageSummary.totalTokens) >= 95
                          ? "bg-base-200 border border-base-300 text-base-content"
                          : "bg-yellow-500/10 border border-yellow-500/30 text-yellow-700 dark:text-yellow-300"
                      }`}
                    >
                      <p className="text-sm font-bold">
                        {usageSummary.totalTokens >= usageSummary.limit
                          ? `⚠️ ${allowanceRefusalMessage(usageSummary.limit === 0 ? "disabled" : "exhausted")}`
                          : getUsagePercentage(usageSummary.totalTokens) >= 95
                            ? "⚠️ You're approaching your monthly token limit"
                            : "📊 High usage detected this month"}
                      </p>
                      <p className="text-xs mt-1">Plan upgrades coming soon.</p>
                    </div>
                  )}
                </div>
              )}

              {!usageSummary && !usageLoading && (
                <div className="text-center py-8">
                  <p className="text-base-content/60">
                    No usage data available for this month
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Goals */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 mt-4 relative">
          {!isPremium && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg">
              <ComingSoon
                featureKey="performanceGoals"
                title="Premium Feature"
              />
            </div>
          )}
          <div
            className={
              !isPremium ? "blur-[1px] pointer-events-none opacity-60" : ""
            }
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-bold text-base-content">
                Performance Goals
              </h3>
              <SparklesIcon
                className={`h-5 w-5 ${isPremium ? "text-base-content/80" : "text-yellow-500"}`}
              />
            </div>
            <p className="mb-4 text-base-content/70 text-sm">
              What athletic performance goal do you have (ie run a PB or win a
              championship)? This guides the AI Insights with personalised
              recommendations and encouragement.
            </p>
            <div className="w-full">
              <label className="block text-sm font-bold text-base-content/80 mb-2">
                Performance Goals
              </label>
              <textarea
                value={performanceGoals}
                onChange={(e) => setPerformanceGoals(e.target.value)}
                onBlur={() => saveGoals()}
                placeholder="Describe your performance goals in your own words..."
                className="w-full resize-none rounded-md p-4 placeholder:text-base-content/60 bg-base-300 focus:outline-none"
                rows={4}
                disabled={!isPremium}
                style={{
                  minHeight: "100px",
                  fontSize: "16px",
                  maxWidth: "100%",
                }}
              />
            </div>
          </div>
        </div>

        {/* Health Goals */}
        <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-6 mt-4 relative">
          {!isPremium && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg">
              <ComingSoon featureKey="healthGoals" />
            </div>
          )}
          <div
            className={
              !isPremium ? "blur-[1px] pointer-events-none opacity-60" : ""
            }
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-bold text-base-content">
                Health Goals
              </h3>
              <SparklesIcon
                className={`h-5 w-5 ${isPremium ? "text-base-content/80" : "text-yellow-500"}`}
              />
            </div>
            <p className="mb-4 text-base-content/70 text-sm">
              What health goals do you have (ie sleep 8 hours a night more
              consistently, reduce stress impact on training)? This guides the
              AI Insights with personalised recommendations and encouragement.
            </p>
            <div className="w-full">
              <label className="block text-sm font-bold text-base-content/80 mb-2">
                Health Goals
              </label>
              <textarea
                value={healthGoals}
                onChange={(e) => setHealthGoals(e.target.value)}
                onBlur={() => saveGoals()}
                placeholder="Describe your health goals in your own words..."
                className="w-full resize-none rounded-md p-4 placeholder:text-base-content/60 bg-base-300 focus:outline-none"
                rows={4}
                disabled={!isPremium}
                style={{
                  minHeight: "100px",
                  fontSize: "16px",
                  maxWidth: "100%",
                }}
              />
            </div>
          </div>
        </div>

        {/* AI Settings Information Dialog */}
        {showToneInfoDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-grotesk font-semibold text-base-content">
                    AI Settings
                  </h3>
                  <button
                    onClick={() => setShowToneInfoDialog(false)}
                    className="p-1 rounded-full hover:bg-base-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5 text-base-content/60" />
                  </button>
                </div>

                <div className="space-y-6 text-base-content/80">
                  {/* Personalized AI Insights Section */}
                  <div>
                    <h4 className="font-semibold text-base-content mb-2">
                      Personalized AI Insights
                    </h4>
                    <p className="text-sm">
                      When enabled, AI-powered insights will appear throughout
                      the app based on your training and wellness data:
                    </p>
                    <ul className="mt-2 space-y-1 text-sm list-disc list-inside ml-2">
                      <li>Dashboard insights and recommendations</li>
                      <li>Training analysis and suggestions</li>
                      <li>Wellness trend observations</li>
                      <li>Performance predictions and guidance</li>
                    </ul>
                    <p className="text-sm mt-2">
                      Disable this setting if you prefer to view only your raw
                      data without AI-generated commentary.
                    </p>
                  </div>

                  {/* Custom Tone Section */}
                  <div className="pt-4 border-t border-base-300">
                    <h4 className="font-semibold text-base-content mb-2">
                      Custom Tone
                    </h4>
                    <p className="text-sm mb-3">
                      Customize how the AI communicates with you by setting a
                      custom tone and style for all AI-generated responses.
                    </p>

                    <p className="text-sm font-semibold mb-2">
                      Example Instructions:
                    </p>
                    <ul className="space-y-1 text-sm list-disc list-inside ml-2">
                      <li>
                        "Talk to me like you're David Goggins - be motivational
                        and intense"
                      </li>
                      <li>"Be gentle, supportive, and encouraging"</li>
                      <li>"Use technical language and be concise"</li>
                      <li>"Be casual and friendly, like a training buddy"</li>
                      <li>"Speak like a professional coach focused on data"</li>
                    </ul>

                    <p className="text-sm mt-3">
                      Your custom tone applies to personalized insights, AI chat
                      conversations, and training recommendations.
                    </p>
                  </div>

                  {/* AI Usage Section */}
                  <div className="pt-4 border-t border-base-300">
                    <h4 className="font-semibold text-base-content mb-2">
                      AI Usage Tracking
                    </h4>
                    <p className="text-sm">
                      Monitor your monthly AI token usage across all features:
                    </p>
                    <ul className="mt-2 space-y-1 text-sm list-disc list-inside ml-2">
                      <li>
                        <strong>Chat Tokens:</strong> Usage from AI chat
                        conversations
                      </li>
                      <li>
                        <strong>Insights Tokens:</strong> Usage from
                        personalized insights generation
                      </li>
                      <li>
                        <strong>Session Builder Tokens:</strong> Usage from
                        building sessions with AI
                      </li>
                      <li>
                        <strong>Monthly Limit:</strong>{" "}
                        {usageSummary
                          ? `${usageSummary.limit.toLocaleString("en-AU")} tokens per month, shared across the Peak apps`
                          : "Shared across the Peak apps"}
                      </li>
                    </ul>
                    <p className="text-sm mt-2">
                      The usage tracker helps you stay within your monthly
                      allocation and understand how you're using AI features.
                    </p>
                  </div>

                  {/* Tip Section */}
                  <div className="pt-4 border-t border-base-300 bg-blue-500/10 -mx-8 px-8 py-4">
                    <p className="text-sm text-base-content/80">
                      <strong>💡 Tip:</strong> For the best experience, enable
                      Personalized AI Insights and set a Custom Tone that
                      matches your communication preferences. This creates a
                      more tailored and engaging experience throughout the app.
                    </p>
                  </div>

                  {/* Okay Button */}
                  <div className="pt-4 -mx-8 -mb-8 px-8 pb-8">
                    <button
                      onClick={() => setShowToneInfoDialog(false)}
                      className="w-full bg-neutral text-white font-grotesk px-4 py-2 rounded-md hover:bg-neutral/90 transition-colors"
                    >
                      Okay
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiSettings;
